<script setup lang="ts">
import { DecimalInput, DecimalInputValue, TextInput } from '@hantera/design-system';
import { formatting, Models, useOrderViewContext } from '@hantera/portal-app';
import Decimal from 'decimal.js';
import { ref } from 'vue';
import { v7 } from 'uuid';

const props = defineProps<{
  orderLine: Models.Graph.OrderLine
  referenceId?: string
}>()

const emit = defineEmits<{
  (event: 'close'): void
}>();

const orderViewContext = useOrderViewContext()
const existingDiscount = props.orderLine.orderLineDiscounts.find(r => r.discountId === props.referenceId)

const descriptionInput = ref(existingDiscount?.description)

existingDiscount

const valueInput = ref<DecimalInputValue>(
  existingDiscount?.discountType === 'percentage' ? {
    isPercentage: true,
    value: existingDiscount?.value.mul(100) ?? new Decimal(0)
  } : {
    isPercentage: false,
    value: existingDiscount?.value ?? new Decimal(0)
  }
)

async function submit() {

  const isPercentage = valueInput.value.isPercentage
  const value = isPercentage ? valueInput.value.value.div(100) : valueInput.value.value

  if (props.referenceId) {
    // Update existing
    orderViewContext.pushPendingChange(
      undefined,
      [{
        type: 'setDiscountDescription',
        discountId: props.referenceId,
        description: descriptionInput.value ?? ''
      }, {
        type: 'setStaticDiscountValue',
        discountId: props.referenceId,
        isPercentage: isPercentage,
        value: value
      }],
      'Updated discount {description} to value {textValue}',
      {
        discountId: props.referenceId,
        description: descriptionInput.value,
        textValue: isPercentage ?
          formatting.percent(valueInput.value.value) :
          formatting.currency(valueInput.value.value, orderViewContext.state.currencyCode),
        value: valueInput.value.value,
        isPercentage: isPercentage
      }
    )
  } else {
    // Create new
    orderViewContext.pushPendingChange(
      undefined,
      [{
        type: 'createStaticOrderLineDiscount',
        discountId: v7(), // Pin discountId for subsequent edits in the same pending save
        orderLineId: props.orderLine.orderLineId,
        description: descriptionInput.value ?? '',
        isPercentage: isPercentage,
        value: value
      }],
      'Created discount {description} of value {textValue} on {productNumnber} ({orderLineNumber})',
      {
        discountId: props.referenceId,
        description: descriptionInput.value,
        textValue: valueInput.value.isPercentage ?
          formatting.percent(valueInput.value.value) :
          formatting.currency(valueInput.value.value, orderViewContext.state.currencyCode),
        value: valueInput.value.value,
        isPercentage: valueInput.value.isPercentage,
        productNumber: props.orderLine.productNumber,
        orderLineNumber: props.orderLine.orderLineNumber,
      }
    )
  }

  emit('close')
}

</script>

<template>
  <form class="edit-form" @submit.prevent.stop="submit">
    <fieldset>
      <div class="field">
        <label>Description</label>
        <TextInput
          v-model="descriptionInput"
          :select-on-focus="true"
        />
      </div>
      <div class="field">
        <label>Value</label>
        <DecimalInput
          v-model="valueInput"
          min="0"
          :allow-percentage="true"
          :select-on-focus="true"
        />
      </div>
      <div class="actions">
        <button class="btn-silent" @click.stop.prevent="() => emit('close')">Cancel</button>
        <button type="submit" class="btn-primary">
          <template v-if="referenceId">Update</template>
          <template v-else>Add</template>
        </button>
      </div>
    </fieldset>
  </form>
</template>

<style scoped>
.actions {
  display: flex;
  gap: 5px;
  justify-content: flex-end;
}
</style>